import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import { isProOrUltraPlan } from "@/components/shared/PaidPlanRoute";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  buildCreateClientPayload,
  CLIENT_COUNTRY_OPTIONS,
  CLIENT_CURRENCY_OPTIONS,
  CLIENT_DOCUMENT_TYPES,
  clientFormSchema,
  CLIENT_PAYMENT_TERM_OPTIONS,
  defaultClientFormValues,
  type ClientFormValues,
} from "@/lib/client-create-form";
import { useAuth } from "@/contexts/AuthContext";
import { clientKeys, tenantKeys } from "@/lib/queryKeys";
import { getTenantCurrent } from "@/services/authService";
import {
  createClient,
  getRucInfoForClient,
  type Client,
} from "@/services/clientService";

export interface AddClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (client: Client) => void;
}

export function AddClientDialog({
  open,
  onOpenChange,
  onCreated,
}: AddClientDialogProps): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: tenant, isFetched: tenantPlanFetched } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });
  const canUseInvoiceEmailTemplates =
    tenantPlanFetched && isProOrUltraPlan(tenant?.planId);

  const [rucInfoLoading, setRucInfoLoading] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ClientFormValues>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: defaultClientFormValues,
  });

  const documentType = watch("documentType");
  const countryCode = watch("countryCode");
  const rutValue = watch("rut");

  const fetchRucInfo = useCallback(() => {
    if (documentType !== "RUT") return;
    const digits = (rutValue ?? "").replace(/\D/g, "");
    if (digits.length !== 12) return;
    const normalized = digits.padStart(12, "0").slice(-12);
    setRucInfoLoading(true);
    getRucInfoForClient(normalized)
      .then((res) => {
        if (!res.valid) {
          toast.error(
            t(
              "clients.form.rucInfoInvalid",
              "Could not load company data for this RUT."
            )
          );
          return;
        }
        const legalNameTrimmed = res.legalName?.trim();
        if (legalNameTrimmed) {
          setValue("legalName", legalNameTrimmed);
          setValue("displayName", legalNameTrimmed);
        }
        if (res.legalAddress?.trim()) setValue("address", res.legalAddress.trim());
        if (res.city?.trim()) setValue("ciudad", res.city.trim());
        if (res.department?.trim()) setValue("departamento", res.department.trim());
        if (res.email?.trim()) setValue("email", res.email.trim());
        if (res.phone?.trim()) setValue("phone", res.phone.trim());
      })
      .catch(() => {
        toast.error(
          t(
            "clients.form.rucInfoError",
            "Could not load company data. Please try again."
          )
        );
      })
      .finally(() => {
        setRucInfoLoading(false);
      });
  }, [documentType, rutValue, setValue, t]);

  useEffect(() => {
    if (!open) return;
    reset(defaultClientFormValues);
  }, [open, reset]);

  useEffect(() => {
    const code = countryCode?.trim().toUpperCase();
    if (code && code !== "UY") {
      setValue("documentType", "EXT");
    }
  }, [countryCode, setValue]);

  const createMutation = useMutation({
    mutationFn: createClient,
    onSuccess: (client) => {
      toast.success(t("clients.quickAdd.success"));
      void queryClient.invalidateQueries({ queryKey: clientKeys.all });
      onCreated(client);
      onOpenChange(false);
    },
    onError: () => {
      toast.error(t("clients.quickAdd.error"));
    },
  });

  const onSubmit = (values: ClientFormValues): void => {
    createMutation.mutate(
      buildCreateClientPayload(values, { canUseInvoiceEmailTemplates })
    );
  };

  const showRutOrCi = documentType === "RUT" || documentType === "CI";
  const showExt = documentType === "EXT";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[min(90vh,720px)] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {t("clients.quickAdd.title")}
          </DialogTitle>
        </DialogHeader>

        <form
          id="add-client-dialog-form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4"
        >
          <div className="relative space-y-3 rounded-lg border p-4">
            {rucInfoLoading && (
              <div
                className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/80 backdrop-blur-[1px]"
                aria-live="polite"
                aria-busy="true"
              >
                <div className="flex flex-col items-center gap-3 rounded-md border bg-card px-6 py-4 shadow-sm">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm font-medium text-foreground">
                    {t("clients.form.rucInfoLoading", "Loading company data…")}
                  </p>
                </div>
              </div>
            )}
            <p className="text-sm font-semibold text-foreground">
              {t("clients.form.legalInfo")}
            </p>
            <div className="space-y-2">
              <Label>{t("clients.form.documentType")} *</Label>
              <Controller
                name="documentType"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="transition-colors duration-150">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLIENT_DOCUMENT_TYPES.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {showRutOrCi && (
              <div className="space-y-2">
                <Label htmlFor="add-client-rut">
                  {documentType === "RUT"
                    ? `${t("clients.form.taxId")} *`
                    : `${t("clients.form.ci")} *`}
                </Label>
                <Input
                  id="add-client-rut"
                  {...register("rut", {
                    onBlur: () => {
                      fetchRucInfo();
                    },
                  })}
                  placeholder={
                    documentType === "RUT"
                      ? t("clients.form.taxIdPlaceholder")
                      : t("clients.form.ciPlaceholder")
                  }
                  maxLength={documentType === "RUT" ? 12 : 8}
                  className={errors.rut ? "border-destructive" : ""}
                />
                {errors.rut && (
                  <p className="text-sm text-destructive">{errors.rut.message}</p>
                )}
              </div>
            )}

            {showExt && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="add-client-country">
                    {t("clients.form.country")}
                  </Label>
                  <Controller
                    name="countryCode"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value ?? ""}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger
                          id="add-client-country"
                          className="transition-colors duration-150"
                        >
                          <SelectValue
                            placeholder={t("clients.form.countryPlaceholder")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {CLIENT_COUNTRY_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-client-doc-ext">
                    {t("clients.form.docRecepExt")} *
                  </Label>
                  <Input
                    id="add-client-doc-ext"
                    {...register("docRecepExt")}
                    placeholder={t("clients.form.docRecepExtPlaceholder")}
                    maxLength={20}
                    className={errors.docRecepExt ? "border-destructive" : ""}
                  />
                  {errors.docRecepExt && (
                    <p className="text-sm text-destructive">
                      {errors.docRecepExt.message}
                    </p>
                  )}
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="add-client-legal">
                {t("clients.form.legalName")} *
              </Label>
              <Input
                id="add-client-legal"
                {...register("legalName")}
                placeholder={t("clients.form.legalNamePlaceholder")}
                className={errors.legalName ? "border-destructive" : ""}
              />
              {errors.legalName && (
                <p className="text-sm text-destructive">
                  {errors.legalName.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-client-display">
                {t("clients.form.displayName")}
              </Label>
              <Input
                id="add-client-display"
                {...register("displayName")}
                placeholder={t("clients.form.displayNamePlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="add-client-address">
                {t("clients.form.address")}
                {documentType === "RUT" ? " *" : null}
              </Label>
              <Input
                id="add-client-address"
                {...register("address")}
                placeholder={
                  documentType === "RUT"
                    ? t("clients.form.addressPlaceholderLegal")
                    : t("clients.form.addressPlaceholder")
                }
                className={errors.address ? "border-destructive" : ""}
              />
              {errors.address && (
                <p className="text-sm text-destructive">
                  {errors.address.message}
                </p>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="add-client-ciudad">
                  {t("clients.form.ciudad", "City")} (
                  {t("clients.form.optionalTag", "optional")})
                </Label>
                <Input
                  id="add-client-ciudad"
                  {...register("ciudad")}
                  maxLength={30}
                  placeholder={t("clients.form.ciudadPlaceholder", "Montevideo")}
                  className="transition-colors duration-150"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-client-departamento">
                  {t("clients.form.departamento", "Department / state")} (
                  {t("clients.form.optionalTag", "optional")})
                </Label>
                <Input
                  id="add-client-departamento"
                  {...register("departamento")}
                  maxLength={30}
                  placeholder={t(
                    "clients.form.departamentoPlaceholder",
                    "Montevideo"
                  )}
                  className="transition-colors duration-150"
                />
              </div>
            </div>
          </div>

          <div className="space-y-3 rounded-lg border p-4">
            <p className="text-sm font-semibold text-foreground">
              {t("clients.form.contactInfo")}
            </p>
            <div className="space-y-2">
              <Label htmlFor="add-client-email">{t("clients.form.email")}</Label>
              <Input
                id="add-client-email"
                type="email"
                {...register("email")}
                placeholder={t("clients.form.emailPlaceholder")}
                className={errors.email ? "border-destructive" : ""}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-client-phone">{t("clients.form.phone")}</Label>
              <Input
                id="add-client-phone"
                {...register("phone")}
                placeholder={t("clients.form.phonePlaceholder")}
              />
            </div>
          </div>

          <div className="space-y-4 rounded-lg border p-4">
            <p className="text-sm font-semibold text-foreground">
              {t("clients.form.invoiceEmailCardTitle")}
            </p>
            <div className="flex items-start space-x-2 space-y-0">
              <Controller
                name="sendInvoiceByEmail"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="add-client-sendInvoiceByEmail"
                    checked={field.value ?? false}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                  />
                )}
              />
              <Label
                htmlFor="add-client-sendInvoiceByEmail"
                className="cursor-pointer text-sm font-normal leading-none"
              >
                {t("clients.form.sendInvoiceByEmail", "Send invoice by email")}
              </Label>
            </div>
            {watch("sendInvoiceByEmail") === true && (
              <div className="flex items-start space-x-2 space-y-0 pl-6">
                <Controller
                  name="autoSendInvoiceEmail"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="add-client-autoSendInvoiceEmail"
                      checked={field.value ?? false}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                  )}
                />
                <Label
                  htmlFor="add-client-autoSendInvoiceEmail"
                  className="cursor-pointer text-sm font-normal leading-none"
                >
                  {t("clients.form.autoSendInvoiceEmail", "Send automatically")}
                </Label>
              </div>
            )}
          </div>

          <div className="space-y-3 rounded-lg border p-4">
            <p className="text-sm font-semibold text-foreground">
              {t("clients.form.invoicingDetails")}
            </p>
            <div className="space-y-2">
              <Label>{t("clients.form.defaultCurrency")}</Label>
              <Controller
                name="defaultCurrency"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ?? "UYU"}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger className="transition-colors duration-150">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLIENT_CURRENCY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("clients.form.paymentType")}</Label>
              <Controller
                name="paymentTermDays"
                control={control}
                render={({ field }) => (
                  <Select
                    value={String(field.value ?? 0)}
                    onValueChange={(v) =>
                      field.onChange(Number(v) as 0 | 30 | 60 | 90)
                    }
                  >
                    <SelectTrigger className="transition-colors duration-150">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLIENT_PAYMENT_TERM_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={String(opt.value)}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-client-discount">
                {t("clients.form.discountPercent")}
              </Label>
              <Input
                id="add-client-discount"
                type="number"
                min={0}
                max={100}
                step={0.5}
                placeholder={t("clients.form.discountPercentPlaceholder")}
                {...register("discountPercent")}
                className={errors.discountPercent ? "border-destructive" : ""}
              />
              {errors.discountPercent && (
                <p className="text-sm text-destructive">
                  {errors.discountPercent.message}
                </p>
              )}
            </div>
          </div>
        </form>

        <DialogFooter className="flex flex-row justify-between gap-2 sm:justify-between">
          <Button
            type="button"
            variant="outline"
            className="transition-colors duration-150"
            onClick={() => onOpenChange(false)}
            disabled={createMutation.isPending || rucInfoLoading}
          >
            {t("clients.quickAdd.cancel")}
          </Button>
          <Button
            type="submit"
            form="add-client-dialog-form"
            disabled={createMutation.isPending || rucInfoLoading}
            className="transition-colors duration-150"
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("clients.quickAdd.saving")}
              </>
            ) : (
              t("clients.quickAdd.submit")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
